
import { ConcernTag } from './JUVTriageQuiz';

export interface Treatment {
  id: string;
  name: string;
  memberPrice: number;
  visitorPrice: number;
  duration: number;
  description: string;
  benefits: string[];
  category: string;
  tags: ConcernTag[];
}

export interface TreatmentBundle {
  id: string;
  name: string;
  price: number;
  originalValue: number;
  treatments: string[];
  description: string;
  tags: ConcernTag[];
}

export class TreatmentRecommendationEngine {
  private treatments: Treatment[] = [
    // Bright Eyes Treatments
    {
      id: 'smootheye-laser',
      name: 'SmoothEye Laser',
      memberPrice: 250,
      visitorPrice: 350,
      duration: 30,
      description: 'Advanced laser treatment for under-eye rejuvenation',
      benefits: ['Reduces dark circles', 'Smooths fine lines', 'Immediate results'],
      category: 'Laser',
      tags: ['BrightEyes']
    },
    {
      id: 'prf-under-eye',
      name: 'PRP Under-Eye Rejuvenation',
      memberPrice: 475,
      visitorPrice: 650,
      duration: 60,
      description: 'Natural regenerative treatment using your own plasma',
      benefits: ['Long-lasting results', 'Natural approach', 'Collagen boost'],
      category: 'Injectable',
      tags: ['BrightEyes']
    },
    {
      id: 'xeomin-crowsfeet',
      name: 'Xeomin Crow\'s Feet',
      memberPrice: 12,
      visitorPrice: 15,
      duration: 15,
      description: 'Precision injectable for crow\'s feet (per unit)',
      benefits: ['Smooth wrinkles', 'Quick treatment', '3-4 month results'],
      category: 'Injectable',
      tags: ['BrightEyes']
    },
    
    // Natural Lip Treatments
    {
      id: 'liplase',
      name: 'LipLase',
      memberPrice: 250,
      visitorPrice: 350,
      duration: 30,
      description: 'Non-invasive laser lip enhancement',
      benefits: ['Natural volume', 'No downtime', 'Subtle enhancement'],
      category: 'Laser',
      tags: ['NaturalLip']
    },
    {
      id: 'lip-flip',
      name: 'Lip Flip (Xeomin)',
      memberPrice: 75,
      visitorPrice: 100,
      duration: 15,
      description: 'Subtle lip enhancement with neuromodulator',
      benefits: ['Natural look', 'Quick procedure', 'Affordable option'],
      category: 'Injectable',
      tags: ['NaturalLip']
    },
    {
      id: 'signature-lip-filler',
      name: 'Signature Lip Filler',
      memberPrice: 699,
      visitorPrice: 899,
      duration: 45,
      description: 'Premium dermal filler for beautiful lip enhancement',
      benefits: ['Dramatic results', 'Long-lasting', 'Expert technique'],
      category: 'Injectable',
      tags: ['NaturalLip']
    },

    // Youth Refresh Treatments
    {
      id: 'rf-microneedling-prp',
      name: 'RF Microneedling w/ PRP',
      memberPrice: 950,
      visitorPrice: 1200,
      duration: 90,
      description: 'Advanced skin rejuvenation combining RF energy with PRP',
      benefits: ['Improves texture', 'Reduces scars', 'Tightens skin'],
      category: 'Facial',
      tags: ['YouthRefresh']
    },
    {
      id: 'laser-skin-rejuvenation',
      name: 'Laser Skin Rejuvenation',
      memberPrice: 275,
      visitorPrice: 350,
      duration: 45,
      description: 'Comprehensive laser treatment for skin renewal',
      benefits: ['Even skin tone', 'Reduces pores', 'Immediate glow'],
      category: 'Laser',
      tags: ['YouthRefresh']
    },
    {
      id: 'perfect-derma-peel',
      name: 'Perfect Derma Peel',
      memberPrice: 299,
      visitorPrice: 399,
      duration: 60,
      description: 'Professional chemical peel for dramatic skin improvement',
      benefits: ['Smooth texture', 'Bright complexion', 'Minimal downtime'],
      category: 'Chemical Peel',
      tags: ['YouthRefresh']
    },

    // Hair Glow Treatments
    {
      id: 'prp-hair-restoration',
      name: 'PRP Hair Restoration',
      memberPrice: 599,
      visitorPrice: 799,
      duration: 60,
      description: 'Natural hair restoration using platelet-rich plasma',
      benefits: ['Stimulates growth', 'Natural approach', 'No surgery'],
      category: 'Injectable',
      tags: ['HairGlow']
    },
    {
      id: 'hairlase-laser',
      name: 'HairLase Laser',
      memberPrice: 275,
      visitorPrice: 350,
      duration: 30,
      description: 'Low-level laser therapy for hair growth stimulation',
      benefits: ['Non-invasive', 'Quick sessions', 'Proven results'],
      category: 'Laser',
      tags: ['HairGlow']
    },

    // Wellness Treatments
    {
      id: 'glow-stack',
      name: 'Glow Stack (GHK-Cu, BPC, TB-500)',
      memberPrice: 350,
      visitorPrice: 450,
      duration: 15,
      description: 'Powerful peptide combination for anti-aging and recovery',
      benefits: ['Increased energy', 'Better recovery', 'Anti-aging'],
      category: 'Peptide',
      tags: ['Wellness']
    },
    {
      id: 'retatrutide',
      name: 'Retatrutide',
      memberPrice: 699,
      visitorPrice: 1150,
      duration: 15,
      description: 'Advanced weight management peptide therapy',
      benefits: ['Weight loss', 'Appetite control', 'Metabolic boost'],
      category: 'Peptide',
      tags: ['Wellness']
    },
    {
      id: 'nad-plus',
      name: 'NAD+',
      memberPrice: 450,
      visitorPrice: 550,
      duration: 30,
      description: 'Cellular energy and longevity booster',
      benefits: ['Increased energy', 'Mental clarity', 'Anti-aging'],
      category: 'Injectable',
      tags: ['Wellness']
    }
  ];

  private bundles: TreatmentBundle[] = [
    {
      id: 'bright-eyes-package',
      name: 'Bright Eyes Package',
      price: 699,
      originalValue: 899,
      treatments: ['smootheye-laser', 'prf-under-eye', 'xeomin-crowsfeet'],
      description: 'Complete under-eye rejuvenation package',
      tags: ['BrightEyes']
    },
    {
      id: 'natural-lip-duo',
      name: 'Natural Lip Duo',
      price: 300,
      originalValue: 450,
      treatments: ['liplase', 'lip-flip'],
      description: 'Perfect combination for natural lip enhancement',
      tags: ['NaturalLip']
    },
    {
      id: 'youth-refresh-package',
      name: 'Youth Refresh',
      price: 1399,
      originalValue: 1700,
      treatments: ['xeomin-crowsfeet', 'rf-microneedling-prp'],
      description: 'Complete facial rejuvenation with injectables and RF',
      tags: ['YouthRefresh']
    },
    {
      id: 'hair-refresh-combo',
      name: 'Hair Refresh',
      price: 799,
      originalValue: 950,
      treatments: ['prp-hair-restoration', 'hairlase-laser'],
      description: 'Comprehensive hair restoration approach',
      tags: ['HairGlow']
    },
    {
      id: 'complete-rejuvenation',
      name: 'Complete Rejuvenation',
      price: 1699,
      originalValue: 2000,
      treatments: ['glow-stack', 'retatrutide', 'nad-plus'],
      description: 'Ultimate wellness and anti-aging package',
      tags: ['Wellness']
    }
  ];

  getRecommendations(concerns: ConcernTag[], budget: string, timeCommitment: string) {
    const recommendations = {
      treatments: [] as Treatment[],
      bundles: [] as TreatmentBundle[]
    };

    // If FullMenu is selected, return popular treatments from all categories
    if (concerns.includes('FullMenu')) {
      recommendations.treatments = this.treatments.slice(0, 6);
      recommendations.bundles = this.bundles.slice(0, 3);
      return recommendations;
    }

    // Get treatments matching selected concerns
    recommendations.treatments = this.treatments.filter(treatment => 
      treatment.tags.some(tag => concerns.includes(tag))
    );

    // Get bundles matching selected concerns
    recommendations.bundles = this.bundles.filter(bundle => 
      bundle.tags.some(tag => concerns.includes(tag))
    );

    // Filter by budget if specified
    if (budget === 'under-500') {
      recommendations.treatments = recommendations.treatments.filter(t => t.memberPrice <= 500);
      recommendations.bundles = recommendations.bundles.filter(b => b.price <= 500);
    } else if (budget === 'under-1000') {
      recommendations.treatments = recommendations.treatments.filter(t => t.memberPrice <= 1000);
      recommendations.bundles = recommendations.bundles.filter(b => b.price <= 1000);
    }

    // Filter by time commitment
    if (timeCommitment === 'quick') {
      recommendations.treatments = recommendations.treatments.filter(t => t.duration <= 30);
    }

    return recommendations;
  }

  getTreatmentById(id: string): Treatment | undefined {
    return this.treatments.find(t => t.id === id);
  }

  getBundleById(id: string): TreatmentBundle | undefined {
    return this.bundles.find(b => b.id === id);
  }
}

export const treatmentEngine = new TreatmentRecommendationEngine();
